import * as actionTypes from '../store/action-types'
import { featureSlugs } from '@/shared/constants/packagebuilder/features'
import { featureProperties } from '@/shared/constants/packagebuilder/featureProperties'
import { formEndingTypes } from '@/shared/constants/formEndingTypes'
import operators from '@/shared/constants/comparisonOperators'

export default {
  data() {
    return {
      count: {
        submission: {
          MONTHLY: 0,
          YEARLY: 0,
          AS_PER_PLAN: 0,
          NONE: 0
        },
        partial: {
          MONTHLY: 0,
          YEARLY: 0,
          AS_PER_PLAN: 0,
          NONE: 0
        },
        lead: {
          MONTHLY: 0,
          YEARLY: 0,
          AS_PER_PLAN: 0,
          NONE: 0
        },
        fileUploadStorage: {
          MONTHLY: 0,
          YEARLY: 0,
          AS_PER_PLAN: 0,
          NONE: 0
        }
      },
      limit: {
        alert: false,
        message: ''
      },
      page: {
        setting: false
      },
      canUseAnswerBased: null,
      hasExceededStorageLimit: false,
    }
  },
  methods: {
    async packageBuilderMixin_partialLeadsCount() {
      try {
        const response = await this.$store.dispatch(
          'formLeads/' + actionTypes.FETCH_PARTIAL_LEADS_COUNT,
          {
            userId: this.user?.id
          }
        )

        if (response?.data?.data) {
          const defaultCounts = {
            MONTHLY: 0,
            YEARLY: 0,
            AS_PER_PLAN: 0,
            NONE: 0
          }

          this.count.partial = {
            ...defaultCounts,
            ...response.data.data
          }
        }
      } catch (error) {
        this.count.partial = {
          MONTHLY: 0,
          YEARLY: 0,
          AS_PER_PLAN: 0,
          NONE: 0
        }
      }
    },

    async packageBuilderMixin_completeLeadCount() {
      try {
        const response = await this.$store.dispatch(
          'formLeads/' + actionTypes.FETCH_COMPLETE_LEADS_COUNT,
          {
            userId: this.user?.id
          }
        )

        if (response?.data?.data) {
          const defaultCounts = {
            MONTHLY: 0,
            YEARLY: 0,
            AS_PER_PLAN: 0,
            NONE: 0
          }

          this.count.lead = {
            ...defaultCounts,
            ...response.data.data
          }
        }
      } catch (error) {
        this.count.lead = {
          MONTHLY: 0,
          YEARLY: 0,
          AS_PER_PLAN: 0,
          NONE: 0
        }
      }
    },

    packageBuilderMixin_formCount() {
      return this.$store.dispatch(actionTypes.FETCH_FORMS_COUNT)
    },

    pBMixin_getFormLimit() {
      // Admin/super-customer: role package limit
      if (this.isAdmin || this.isSuperCustomer) {
        const roleLimit = this.user?.role_package_limits?.find(
          item => item?.property_title === featureProperties.NO_OF_FORMS
        )
        const parsedRoleLimit = Number.parseInt(roleLimit?.value, 10)
        return Number.isFinite(parsedRoleLimit) ? parsedRoleLimit : 1
      }

      // Regular user: plan feature property
      const formsFeature = this.plan?.plan_features?.find(
        feature => feature?.slug === featureSlugs.FORMS
      )
      const formsLimitProperty = formsFeature?.feature_properties?.find(
        property => property?.title === featureProperties.NO_OF_FORMS
      )
      const parsedFeatureLimit = Number.parseInt(formsLimitProperty?.value, 10)

      if (Number.isFinite(parsedFeatureLimit)) {
        return parsedFeatureLimit
      }

      return 1
    },

    pBMixin_hasFormLimit(currentCount = 0) {
      return this.pBMixin_getFormLimit() > Number(currentCount || 0)
    },

    async packageBuilderMixin_canCreateSubmission() {
      this.limit.message = ''
      this.limit.alert = false
      this.page.setting = false

      // Fetch counts for both complete and partial leads
      await Promise.all([
        this.packageBuilderMixin_partialLeadsCount(),
        this.packageBuilderMixin_completeLeadCount(),
      ])

      // Calculate combined submission counts
      this.count.submission = {
        NONE: (this.count?.partial?.NONE || 0) + (this.count?.lead?.NONE || 0),
        MONTHLY: (this.count?.partial?.MONTHLY || 0) + (this.count?.lead?.MONTHLY || 0),
        YEARLY: (this.count?.partial?.YEARLY || 0) + (this.count?.lead?.YEARLY || 0),
        AS_PER_PLAN: (this.count?.partial?.AS_PER_PLAN || 0) + (this.count?.lead?.AS_PER_PLAN || 0)
      }

      const submissionAccess = this.userMixin_hasFeatureAccess?.(featureSlugs.SUBMISSIONS)

      if (!submissionAccess) {
        this.setLimitMessage("You don't have access to submissions.")
        return false
      }

      const operator = operators.AND

      const inputArray = [
        {
          limitation_title: featureProperties.NO_OF_SUBMISSIONS,
          limitation_value: this.count?.submission?.NONE,
          limitation_valueMonthly: this.count?.submission?.MONTHLY,
          limitation_valueYearly: this.count?.submission?.YEARLY,
          limitation_valueAsPerPlan: this.count?.submission?.AS_PER_PLAN,
          compare: operators.LT
        }
      ]

      try {
        const canCreateSubmission = await this.userMixin_hasFeaturePropertyAccess?.(
          featureSlugs.SUBMISSIONS,
          operator,
          inputArray
        )

        if (!canCreateSubmission) {
          this.setLimitMessage('You have crossed the submissions limit.')
        }

        return canCreateSubmission
      } catch (error) {
        this.limit.alert = false
        return false
      }
    },

    packageBuilderMixin_canUseMultipleEndings() {
      return this.userMixin_hasFeatureAccess?.(featureSlugs.MULTIPLE_ENDINGS)
    },

    getPlanFeatureProperties(plan, featureSlug) {
      const planFeatures = plan?.plan_features?.find(
        (elm) => elm?.slug === featureSlug
      )
      if (!planFeatures) {
        return []
      }
      return planFeatures?.feature_properties?.filter(
        (element) => element?.feature_id === planFeatures?.id
      ) || []
    },

    packageBuilderMixin_canUseEndingType(endingType) {
      if (this.isAdmin || this.isSuperCustomer) {
        return true
      }

      const planFeatureProperties = this.getPlanFeatureProperties(
        this.plan,
        featureSlugs.MULTIPLE_ENDINGS
      )

      const endingTypeToFeatureProperty = {
        [this.endingTypes?.ANSWERBASED]: featureProperties.HAS_ANSWERBASED,
        [this.endingTypes?.VISITORBASED]: featureProperties.HAS_VISITORBASED,
        [this.endingTypes?.SCOREBASED]: featureProperties.HAS_SCOREBASED
      }

      const featureProperty = endingTypeToFeatureProperty[endingType]
      const hasEndingType = this.getValueByTitle(
        planFeatureProperties,
        featureProperty
      )

      return parseInt(hasEndingType, 10) || 0
    },

    packageBuilderMixin_canUseTrustedForm() {
      if (this.isAdmin || this.isSuperCustomer) {
        return true
      }

      const planFeatureProperties = this.getPlanFeatureProperties(
        this.plan,
        featureSlugs.TRUSTEDFORM
      )

      const hasTrustedForm = this.getValueByTitle(
        planFeatureProperties,
        featureProperties.ENABLE_TRUSTEDFORM
      )

      return parseInt(hasTrustedForm, 10) || 0
    },

    packageBuilderMixin_canUseSignature() {
      if (this.isAdmin || this.isSuperCustomer) {
        return true
      }

      const planFeatureProperties = this.getPlanFeatureProperties(
        this.plan,
        featureSlugs.SIGNATURE
      )

      const hasSignature = this.getValueByTitle(
        planFeatureProperties,
        featureProperties.ENABLE_SIGNATURE
      )

      return parseInt(hasSignature, 10) || 0
    },

    packageBuilderMixin_canUsePayment() {
      if (this.isAdmin || this.isSuperCustomer) {
        return true
      }

      const planFeatureProperties = this.getPlanFeatureProperties(
        this.plan,
        featureSlugs.PAYMENT
      )

      const hasPayment = this.getValueByTitle(
        planFeatureProperties,
        featureProperties.ENABLE_PAYMENT
      )

      return parseInt(hasPayment, 10) || 0
    },

    async packageBuilderMixin_fileUploadStorage() {
      try {
        const response = await this.$store.dispatch(
          'media/' + actionTypes.FETCH_FILE_UPLOAD_STORAGE_COUNT)
        if (response?.data) {
          this.hasExceededStorageLimit = response?.data?.usage_percentage >= 100
          const totalSizeMB = response?.data?.total_size_mb || 0

          this.count.fileUploadStorage = {
            MONTHLY: 0,
            YEARLY: 0,
            AS_PER_PLAN: 0,
            NONE: totalSizeMB
          }
        }
      } catch (error) {
        this.count.fileUploadStorage = {
          MONTHLY: 0,
          YEARLY: 0,
          AS_PER_PLAN: 0,
          NONE: 0
        }
      }
    },

    getValueByTitle(data, featureProperty) {
      const featurePropertyValue = data?.find(
        (obj) => obj?.title === featureProperty
      )
      return featurePropertyValue?.value
    },

    setLimitMessage(message) {
      this.limit.alert = true
      this.limit.message = message
    }
  },
  computed: {
    endingTypes() {
      return formEndingTypes
    }
  }
}
